import Image from 'next/image';

import { ImageCopyright } from '../../../design-system/components/image/copyright/ImageCopyright';
import { RATIO, Ratio } from '../../../design-system/components/ratio/Ratio';
import { DestinationArticleTag } from '../../../design-system/components/tag/destination-article-tag/DestinationArticleTag';
import { Typography } from '../../../design-system/components/typography/Typography';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { imageLoaderForPicture } from '../../../domains/Layout/Image/URL/ImageURL';
import { Author } from '../../../resources/author/domain/types/author';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { DestinationArticle } from '../../../resources/destinationArticles/domain/types/destinationArticle';
import { ArticleAuthorWithDate } from '../../../shared/components/ArticleAuthorWithDate/ArticleAuthorWithDate';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './DestinationPracticalInformationArticle.module.css';

type DestinationPracticalInformationProps = {
    destination: PickDomain<Destination, 'id' | 'slug'>;
    article: PickDomain<
        DestinationArticle,
        'title' | 'coverPicture' | 'introduction' | 'content' | 'type' | 'updatedAt'
    >;
    author: PickDomain<Author, 'firstName' | 'lastName' | 'id' | 'slug'> | null;
};

export function DestinationPracticalInformationArticle({
    article,
    destination,
    author,
}: DestinationPracticalInformationProps) {
    const { coverPicture } = article;
    const pictureRatio = RATIO['16/9'];

    return (
        <article className={style.container}>
            <div className={style.articleHead}>
                <DestinationArticleTag
                    size="medium"
                    articleType={article.type}
                    destination={destination}
                />
                <Typography as="h1" scale="secondary-m-bold">
                    {article.title}
                </Typography>

                {author ? <ArticleAuthorWithDate author={author} date={article.updatedAt} /> : null}
            </div>

            <HtmlContent htmlContent={article.introduction} />

            <Ratio ratio={pictureRatio}>
                <Image
                    alt=""
                    src={coverPicture.id}
                    fill={true}
                    loading="eager"
                    priority={true}
                    sizes="(min-width: 576px) 500px, (min-width: 768px) 600px, 100vw"
                    loader={imageLoaderForPicture(coverPicture, {
                        aspectRatio: pictureRatio,
                    })}
                />
                {coverPicture.author ? <ImageCopyright copyright={coverPicture.author} /> : null}
            </Ratio>

            <HtmlContent htmlContent={article.content} />
        </article>
    );
}
